import React, { useCallback, useState } from "react";
import { Avatar, Box, Typography, IconButton, Menu, MenuItem, Chip, Switch, Tooltip, useTheme } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";
import { MbscResource } from "@mobiscroll/react";
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ProviderTypeChip from "./ProviderTypeChip";
import { LocalShift } from "~/hooks/visitDragDropReducer";
import { UNASSIGNED_PROVIDER_TYPE_ID } from "~/utils/visitsHelper";

export const OPTIMIZE_THIS_PROVIDER_HELP =
  "Visits that do not include this provider are locked. This provider's visit times can change freely, and the visit stays with this provider. Other providers on those same visits stay assigned as they are, and changes to their times are kept as small as possible.";

interface ProviderCalendarItemProps {
  resource: MbscResource;
  loading: boolean;
  handleViewRoutes: (providerId: string, name: string) => void;
  handleOnAddVisit: (providerId: number) => void;
  onOptimizeProvider: (providerId: number, ignoreShouldKeepOptimizationResult: boolean) => void;
  optimizeDisabled: boolean;
  isCareTeam?: boolean;
  providerShifts?: LocalShift[];
  onGroupToggle?: (typeId: number) => void;
}

const ProviderCalendarItem: React.FC<ProviderCalendarItemProps> = ({
  resource,
  loading,
  handleViewRoutes,
  handleOnAddVisit,
  onOptimizeProvider,
  optimizeDisabled,
  isCareTeam,
  providerShifts,
  onGroupToggle,
}) => {
  const [providerAnchorElem, setProviderAnchorElem] = useState<null | HTMLElement>(null);
  const [ignoreShouldKeepOptimizationResult, setIgnoreShouldKeepOptimizationResult] = useState(false);

  const handleProvidersMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setProviderAnchorElem(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setProviderAnchorElem(null);
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    handleProvidersMenuOpen(event);
  };

  const handleGroupClick = useCallback(() => {
    if (onGroupToggle && resource.typeId) {
      onGroupToggle(resource.typeId);
    }
  }, [onGroupToggle, resource.typeId]);
  
  if (resource.isParent) {
    return <ProviderTypeGroup name={resource.name || ""} onClick={handleGroupClick} collapsed={resource.collapsed ?? false} />
  }

  const isPlaceholder = resource.id == `${UNASSIGNED_PROVIDER_TYPE_ID}`
  const theme = useTheme()

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      {isCareTeam && (
        <Box 
          sx={{ 
            position: "absolute", 
            top: "-4px", 
            left: "-5px", 
            borderRadius: "10px", 
            padding: "0 2px",
            zIndex: 10,
            width: "100%",
          }}
        >
          <Chip
            icon={<AutoModeIcon sx={{ color: "white !important", fontSize: "12px !important" }} />}
            label="Care Team"
            size="small"
            color="primary"
            sx={{
              color: "white",
              fontSize: "8px",
              width: "106%",
              maxWidth: "inherit",
              borderRadius: "0",
              paddingTop: "3px",
              "& .MuiChip-label": {
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                paddingTop: "3px",
              },
              "& .MuiChip-icon": {
                marginLeft: "2px",
                marginRight: "-2px",
              },
            }}
          />
        </Box>
      )}
      <Box sx={{ display: "flex", paddingTop: "10px", flexDirection: "row", alignSelf: "inherit", }}>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Avatar
            sx={{ bgcolor: resource.active ? theme.palette.primary.light : 'gray', fontSize: 14, width: 30, height: 30 }}
            src=""
            alt={`${resource.firstName || ''} ${resource.lastName || ''}`}
          >
            {(resource.firstName || '').charAt(0)}
            {(resource.lastName || '').charAt(0)}
          </Avatar>
        </Box>
        <Typography sx={{ ml: 1, fontSize: "14px", fontWeight: 'normal' }} component="div">
          {resource.name}
          {!loading && !isPlaceholder && (
            <ProviderTypeChip
              providerTypeName={resource.providerType?.name}
              shifts={providerShifts}
            />
          )}
          {!loading && !resource.active && (
            <Chip
              size="small"
              label={'Inactive'}
              sx={{
                fontSize: 11,
                marginTop: 1
              }}
            />
          )}
        </Typography>
        {loading || isPlaceholder || !resource.active ? null : <>
          <IconButton sx={{ ml: -1 }} size="small" onClick={(event) => handleMenuOpen(event)}>
            <ArrowDropDownIcon />
          </IconButton>
          <Menu
            anchorEl={providerAnchorElem}
            open={Boolean(providerAnchorElem)}
            onClose={handleMenuClose}
          >
            <PermissionOrHide permission={Permission.createOverrideVisits}>
              <MenuItem onClick={() => {
                handleOnAddVisit(Number(resource?.id));
                handleMenuClose();
              }}>Add Visit</MenuItem>
            </PermissionOrHide>
            <MenuItem onClick={() => {
              handleViewRoutes(resource.id.toString(), `${resource?.name}`);
              handleMenuClose();
            }}>View Route</MenuItem>
            <PermissionOrHide permission={Permission.optimizeSchedule}>
              <Box sx={{ display: "flex", alignItems: "center", pr: 0.5 }}>
                <MenuItem
                  disabled={optimizeDisabled}
                  sx={{ flexGrow: 1 }}
                  onClick={() => {
                    onOptimizeProvider(Number(resource.id), ignoreShouldKeepOptimizationResult);
                    handleMenuClose();
                  }}
                >
                  Optimize This Provider
                </MenuItem>
                <Tooltip title={OPTIMIZE_THIS_PROVIDER_HELP}>
                  <IconButton
                    size="small"
                    aria-label="About Optimize This Provider"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            </PermissionOrHide>
            <PermissionOrHide permission={Permission.debugApps}>
              <Box
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIgnoreShouldKeepOptimizationResult(!ignoreShouldKeepOptimizationResult);
                }}
                sx={{
                  px: 2,
                  py: 1,
                  mt: 0.5,
                  borderTop: 1,
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  cursor: "pointer",
                }}
              >
                <Typography variant="inherit">
                  Ignore keep-results check
                </Typography>
                <Switch
                  size="small"
                  checked={ignoreShouldKeepOptimizationResult}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onChange={(event) => {
                    setIgnoreShouldKeepOptimizationResult(event.target.checked);
                    event.stopPropagation();
                  }}
                />
              </Box>
            </PermissionOrHide>
          </Menu>
        </>}
      </Box>
    </Box>
  );
};

export default ProviderCalendarItem;

const ProviderTypeGroup = ({ collapsed, name, onClick }: { name: string, onClick: () => void, collapsed: boolean }) => {
  return <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", cursor: "pointer", p: 1 }} onClick={onClick}>
    <Typography sx={{ fontWeight: 'bold' }} component="div">
      {name}
    </Typography>
    {collapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowDownIcon />}
  </Box>
}
